import axios from "axios";
import AppHeader from "components/AppHeader";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import DebitNoteResponsiveHeader from "layouts/DebitNote/DebitNoteResponsiveHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LuArrowLeft,
  LuFileClock,
  LuLock,
  LuPlus,
  LuReceiptText,
  LuSearch,
} from "react-icons/lu";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchActivityLog, saveNotes } from "utils/api";
import { appConstants } from "utils/appConstants";
import { showResMsg } from "utils/generic";
import {
  includeLegacyInvoiceDelivery,
  isInternalNoteActivity,
} from "utils/invoiceHistory";
import { formatActivityTimestamp } from "utils/activityTimestamp";
import { getRCTIByID } from "utils/invoiceAPI";
import { resolvePreviewReturn } from "utils/previewReturn";
import { getQuotationDetailsByID } from "utils/quotationAPI";
import "./invoiceFlow.css";
import "./invoiceHistory.css";

const isNoteRow = isInternalNoteActivity;
const HISTORY_PAGE_SIZE = 10;

export default function InvoiceHistory({ documentType = "invoice" }) {
  const { id: documentID } = useParams();
  const isQuotation = documentType === "quotation";
  const isRcti = documentType === "rcti";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [navOpen, setNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState({});
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const loginDetails = useMemo(
    () => JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}"),
    []
  );
  const organisation =
    loginDetails.companyName ||
    loginDetails.businessName ||
    loginDetails.organisationName ||
    loginDetails.organizationName ||
    loginDetails.fullName ||
    loginDetails.name ||
    "My Organisation";
  const currentUser =
    `${loginDetails.firstName || ""} ${loginDetails.lastName || ""}`.trim() ||
    "User";
  const fallbackReturnPath = isQuotation
    ? `/quotations/view/${documentID}`
    : isRcti
    ? `/rcti/view/${documentID}`
    : `/invoices/view/${documentID}`;
  const fallbackReturnLabel = `Back to ${
    isQuotation ? "quotation" : isRcti ? "RCTI" : "invoice"
  }`;
  const historyReturn = resolvePreviewReturn(
    location,
    fallbackReturnPath,
    fallbackReturnLabel
  );

  const requestHistory = useCallback(
    () =>
      new Promise((resolve) => {
        if (!documentID || !loginDetails.businessUserID) {
          resolve([]);
          return;
        }
        fetchActivityLog(
          {
            _id: documentID,
            businessUserID: loginDetails.businessUserID,
            userID: loginDetails._id,
            token: loginDetails.token,
            screenType: isQuotation
              ? "Quotation"
              : isRcti
              ? "RCTI"
              : "Invoices",
            invoiceStatus: "All",
            page: 1,
            limit: 100,
            perPage: 100,
            filterValues: "",
            sortingField: "dateAndTime",
            sortingType: -1,
            searchParameterList: ["dateAndTime", "name", "action", "details"],
          },
          (data) => resolve(data?.Result_Output?.docs || []),
          dispatch,
          () => resolve([])
        );
      }),
    [dispatch, documentID, isQuotation, isRcti, loginDetails]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const requestDocument = () => {
        if (isQuotation) {
          return new Promise((resolve) => {
            getQuotationDetailsByID(
              {
                quotationID: documentID,
                businessUserID: loginDetails.businessUserID,
              },
              (data) => {
                const output = data?.Result_Output;
                resolve(Array.isArray(output) ? output[0] : output);
              },
              dispatch,
              () => resolve(null)
            );
          });
        }
        if (isRcti) {
          return new Promise((resolve) => {
            getRCTIByID(
              { rctiID: documentID },
              (data) => {
                const output = data?.Result_Output;
                resolve(Array.isArray(output) ? output[0] : output);
              },
              dispatch,
              () => resolve(null)
            );
          });
        }
        return axios
          .post(`${appConstants.baseURL}invoice/viewInvoiceByID`, {
            invoiceID: documentID,
          })
          .then(({ data }) => {
            const output = data?.Result_Output;
            if (!data?.Result_Status) return null;
            return Array.isArray(output) ? output[0] : output;
          })
          .catch(() => null);
      };

      const [record, historyRows] = await Promise.all([
        requestDocument(),
        requestHistory(),
      ]);
      if (!active) return;
      if (record) setDocument(record);
      else {
        const label = isQuotation ? "quotation" : isRcti ? "RCTI" : "invoice";
        showResMsg(`Unable to load ${label} history.`, 2200, false);
      }
      setRows(historyRows);
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [
    dispatch,
    documentID,
    isQuotation,
    isRcti,
    requestHistory,
    loginDetails.businessUserID,
  ]);

  const detailedRows = useMemo(
    () =>
      isRcti
        ? rows
        : includeLegacyInvoiceDelivery(rows, document, currentUser),
    [currentUser, document, isRcti, rows]
  );

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return detailedRows.filter((row) => {
      if (filter === "notes" && !isNoteRow(row)) return false;
      if (!query) return true;
      return [row.action, row.name, row.details, row.dateAndTime]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [detailedRows, filter, search]);
  const totalPages = Math.max(
    1,
    Math.ceil(visibleRows.length / HISTORY_PAGE_SIZE)
  );
  const paginatedRows = useMemo(
    () =>
      visibleRows.slice(
        (page - 1) * HISTORY_PAGE_SIZE,
        page * HISTORY_PAGE_SIZE
      ),
    [page, visibleRows]
  );

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleAddNote = () => {
    const details = note.trim();
    if (!details || savingNote) return;
    setSavingNote(true);
    saveNotes(
      {
        _id: documentID,
        businessUserID: loginDetails.businessUserID,
        userID: loginDetails._id,
        token: loginDetails.token,
        details,
        screenType: isQuotation
          ? "Quotation"
          : isRcti
          ? "RCTI"
          : "Invoices",
      },
      (data) => {
        setSavingNote(false);
        if (data?.Result_Status) {
          setNote("");
          requestHistory().then(setRows);
          showResMsg("Internal note added", 1400, true);
        } else {
          showResMsg(
            data?.Result_Message || "Unable to add note.",
            2000,
            false
          );
        }
      },
      dispatch
    );
  };

  return (
    <div
      className={`invoice-flow-shell invoice-history-shell${
        darkMode ? " dark" : ""
      }`}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath={
          isQuotation ? "/quotations" : isRcti ? "/rcti" : "/invoices"
        }
        organisation={organisation}
        role={loginDetails.roleName || "Owner"}
      />
      <div className="invoice-flow-main">
        <AppHeader onOpenNav={() => setNavOpen(true)} userName={currentUser} />
        <main className="invoice-history-page">
          <div className="invoice-detail-orgbar">
            <DebitNoteResponsiveHeader
              organisation={organisation}
              role={loginDetails.roleName || "Owner"}
            />
          </div>
          <button
            type="button"
            className="invoice-flow-back"
            onClick={() => navigate(historyReturn.path)}
          >
            <LuArrowLeft /> {historyReturn.label}
          </button>

          {loading ? (
            <PageSkeleton
              type="history"
              label={`Loading ${
                isQuotation ? "quotation" : isRcti ? "RCTI" : "invoice"
              } history…`}
            />
          ) : (
            <>
              <header className="invoice-history-page-header">
                <div>
                  <span className="invoice-history-eyebrow">
                    {isQuotation
                      ? "Quotation"
                      : isRcti
                      ? "RCTI"
                      : "Invoice"}{" "}
                    activity
                  </span>
                  <h1>History &amp; notes</h1>
                  <p>
                    Audit events are permanent. Internal notes are visible only
                    to your team.
                  </p>
                </div>
                <div className="invoice-history-document-chip">
                  <LuReceiptText />
                  <div>
                    <strong>
                      {isQuotation
                        ? document.quotationNumber || "Quotation"
                        : isRcti
                        ? document.rctiNumber || document.invoiceNumber || "RCTI"
                        : document.invoiceNumber || "Invoice"}
                    </strong>
                    <span>
                      {document.contactName ||
                        document.customerName ||
                        document.supplierName ||
                        (isRcti ? "Supplier" : "Customer")}
                    </span>
                  </div>
                  <span className="invoice-history-status">
                    {(isQuotation
                      ? document.quotationStatus
                      : isRcti
                      ? document.status || document.rctiStatus
                      : document.invoiceStatus) || "Draft"}
                  </span>
                </div>
              </header>

              <section className="invoice-history-private-banner">
                <LuLock />
                <div>
                  <strong>Only visible to your team</strong>
                  <p>
                    History and internal notes never appear on the customer{" "}
                    {isQuotation
                      ? "quotation"
                      : isRcti
                      ? "RCTI"
                      : "invoice"} or PDF.
                  </p>
                </div>
              </section>

              <section className="invoice-history-workspace">
                <div className="invoice-history-feed-panel">
                  <div className="invoice-history-toolbar">
                    <div
                      className="invoice-history-tabs"
                      role="tablist"
                      aria-label="History filters"
                    >
                      <button
                        type="button"
                        className={filter === "all" ? "active" : ""}
                        onClick={() => setFilter("all")}
                      >
                        All activity <span>{detailedRows.length}</span>
                      </button>
                      <button
                        type="button"
                        className={filter === "notes" ? "active" : ""}
                        onClick={() => setFilter("notes")}
                      >
                        Notes{" "}
                        <span>{detailedRows.filter(isNoteRow).length}</span>
                      </button>
                    </div>
                    <label className="invoice-history-search">
                      <LuSearch />
                      <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search history"
                        aria-label={`Search ${
                          isQuotation
                            ? "quotation"
                            : isRcti
                            ? "RCTI"
                            : "invoice"
                        } history`}
                      />
                    </label>
                  </div>

                  <div className="invoice-history-feed">
                    {visibleRows.length ? (
                      paginatedRows.map((row, index) => (
                        <article
                          className={`invoice-history-event${
                            isNoteRow(row) ? " is-note" : ""
                          }`}
                          key={row._id || `${row.dateAndTime}-${index}`}
                        >
                          <div className="invoice-history-event-icon">
                            {isNoteRow(row) ? <LuLock /> : <LuFileClock />}
                          </div>
                          <div className="invoice-history-event-body">
                            <div className="invoice-history-event-heading">
                              <strong>
                                {row.action ||
                                  (isNoteRow(row)
                                    ? "Internal note"
                                    : "Invoice updated")}
                              </strong>
                              <time>
                                {formatActivityTimestamp(row.dateAndTime)}
                              </time>
                            </div>
                            <span>{row.name || "Team member"}</span>
                            <p>{row.details || "Invoice activity recorded."}</p>
                          </div>
                        </article>
                      ))
                    ) : (
                      <div className="invoice-history-empty">
                        <LuFileClock />
                        <strong>No matching activity</strong>
                        <p>Try another search or history filter.</p>
                      </div>
                    )}
                  </div>
                  {visibleRows.length > HISTORY_PAGE_SIZE && (
                    <nav
                      className="invoice-history-pagination"
                      aria-label="History pagination"
                    >
                      <span>
                        Showing {(page - 1) * HISTORY_PAGE_SIZE + 1}-
                        {Math.min(page * HISTORY_PAGE_SIZE, visibleRows.length)}{" "}
                        of {visibleRows.length}
                      </span>
                      <div>
                        <button
                          type="button"
                          disabled={page === 1}
                          onClick={() => setPage((current) => current - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {page} of {totalPages}
                        </span>
                        <button
                          type="button"
                          disabled={page === totalPages}
                          onClick={() => setPage((current) => current + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </nav>
                  )}
                </div>

                <aside className="invoice-history-note-panel">
                  <div>
                    <h2>Add an internal note</h2>
                    <p>
                      Keep context for your team without changing the customer{" "}
                      {isQuotation
                        ? "quotation"
                        : isRcti
                        ? "RCTI"
                        : "invoice"}
                      .
                    </p>
                  </div>
                  <label htmlFor="invoice-history-note">Note</label>
                  <textarea
                    id="invoice-history-note"
                    value={note}
                    maxLength={1000}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Write a note for your team…"
                  />
                  <div className="invoice-history-note-meta">
                    <span>Only visible to your team</span>
                    <span>{note.length}/1000</span>
                  </div>
                  <button
                    type="button"
                    disabled={!note.trim() || savingNote}
                    onClick={handleAddNote}
                  >
                    <LuPlus /> {savingNote ? "Adding…" : "Add note"}
                  </button>
                </aside>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
